import type { QuizScript } from '@quebon/shared-learning'
import type { PrintBookDetail } from '@/api/printBook'

export interface InvalidQuizEntry {
  lessonId: number
  lessonName: string
  /** 0=step1, 1=step2, 2=step3, 3=step4 */
  stepIdx: number
  /** lesson 전체(step1~step4 통합) 1-based 순번 */
  quizNumberInLesson: number
  quizId: string
  issues: QuizValidationIssue[]
}

export type QuizValidationIssue = 'sampleValue' | 'answersOrder'

export interface ValidatableLesson {
  lessonId: number
  lessonName: string
  steps: QuizScript[][]      // step1, step2, step3
  step4Quizzes: QuizScript[]
}

function isAnswersOrderRequired(script: QuizScript): boolean {
  return script.type === 'ordering' || script.type === 'pairing'
}

export function getQuizValidationIssues(script: QuizScript): QuizValidationIssue[] {
  const issues: QuizValidationIssue[] = []
  if (script.hasVars && !script.hasVarsWithSampleValue) {
    issues.push('sampleValue')
  }
  if (isAnswersOrderRequired(script) && !script.hasAnswersOrder) {
    issues.push('answersOrder')
  }
  return issues
}

export function formatQuizValidationIssues(issues: QuizValidationIssue[]): string {
  const labels = issues.map(issue => issue === 'sampleValue' ? 'sample value' : 'answersorder')
  return labels.join(', ')
}

/**
 * 책에 포함된 모든 lesson 의 모든 step(1~4) 퀴즈를 순회하며,
 * sample value 또는 answersorder 누락 quiz 를 invalid 로 수집한다.
 *
 * "값 없음" 정의: backend 가 null / "" / "{}" 를 모두 false 로 보내므로
 * frontend 는 단순히 boolean 플래그를 조합해 판단한다.
 *
 * `hasVars` / `hasVarsWithSampleValue` 가 응답에 없으면(undefined/null) false 로
 * 취급되어 sample value 검증을 통과시킨다. 순서 고정 대상 유형에서
 * `hasAnswersOrder` 가 없으면 answersorder 누락으로 안내한다.
 */
export function collectInvalidQuizzes(
  lessonDataMap: Map<number, ValidatableLesson>,
  bookDetail: PrintBookDetail | undefined,
): InvalidQuizEntry[] {
  if (!bookDetail) return []
  const invalid: InvalidQuizEntry[] = []
  bookDetail.modules.forEach(mod => {
    mod.lessons.forEach(lessonMeta => {
      const lesson = lessonDataMap.get(lessonMeta.lessonId)
      if (!lesson) return
      let runningNumber = 0
      const allStepArrays: QuizScript[][] = [...lesson.steps, lesson.step4Quizzes]
      allStepArrays.forEach((stepQuizzes, stepIdx) => {
        stepQuizzes.forEach(script => {
          runningNumber++
          const issues = getQuizValidationIssues(script)
          if (issues.length > 0) {
            invalid.push({
              lessonId: lesson.lessonId,
              lessonName: lesson.lessonName,
              stepIdx,
              quizNumberInLesson: runningNumber,
              quizId: String(script.quizId ?? ''),
              issues,
            })
          }
        })
      })
    })
  })
  return invalid
}

export function stepLabelOf(stepIdx: number): string {
  return stepIdx === 3 ? '4' : String(stepIdx + 1)
}
